import { Link } from "react-router-dom";
import { Calendar, Clock, ArrowRight } from "lucide-react";
import { useBlog } from "@/lib/blog";
import Reveal from "@/components/site/Reveal";
import SectionCTA from "@/components/site/SectionCTA";
import NewsletterSignup from "@/components/site/NewsletterSignup";
import useSeo from "@/lib/useSeo";
import { useT, useLang } from "@/lib/i18n";

const Blog = () => {
  const t = useT();
  const lang = useLang();
  const { articles } = useBlog();
  const locale = lang === "ca" ? "ca-ES" : "es-ES";
  const formatDate = (iso) => new Date(iso).toLocaleDateString(locale, { day: "numeric", month: "long", year: "numeric" });

  useSeo({
    title: {
      es: "Blog · Acompañamiento Oncológico Cuerpo-Mente | Método TAO",
      ca: "Blog · Acompanyament Oncològic Cos-Ment | Mètode TAO",
    },
    description: {
      es: "Artículos sobre yoga oncológico, fibromialgia, fatiga crónica y acompañamiento cuerpo-mente. Recursos prácticos desde la experiencia profesional y vivida de Marta Montardit.",
      ca: "Articles sobre ioga oncològic, fibromiàlgia, fatiga crònica i acompanyament cos-ment. Recursos pràctics des de l'experiència professional i viscuda de la Marta Montardit.",
    },
    path: "/blog",
  });

  return (
    <div className="bg-ivory">
      <section className="pt-12 md:pt-20 pb-16" data-testid="page-blog">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12 lg:px-16">
          <Reveal>
            <p className="text-[11px] tracking-[0.32em] uppercase text-sage mb-6">{t("blog.eyebrow")}</p>
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl text-charcoal leading-[0.98] max-w-3xl">
              {t("blog.title1")} <span className="italic text-terracotta">{t("blog.title2")}</span>.
            </h1>
            <p className="mt-7 text-lg md:text-xl leading-relaxed text-charcoal/75 font-light max-w-2xl">
              {t("blog.lead")}
            </p>
          </Reveal>
        </div>
      </section>

      <section className="pb-20 md:pb-28">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12 lg:px-16">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
            {articles.map((a, i) => (
              <Reveal key={a.slug} delay={i * 0.08}>
                <Link
                  to={`/blog/${a.slug}`}
                  data-testid={`blog-card-${a.slug}`}
                  className="group block"
                >
                  <article className="bg-white rounded-3xl overflow-hidden border border-sand/60 hover:shadow-xl hover:shadow-charcoal/10 transition-all h-full flex flex-col">
                    <div className="aspect-[4/3] overflow-hidden">
                      <img
                        src={a.coverImage}
                        alt={a.title}
                        className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-700"
                        loading="lazy"
                        decoding="async"
                      />
                    </div>
                    <div className="p-7 flex-1 flex flex-col">
                      <span className="text-[10px] tracking-[0.3em] uppercase text-sage mb-3">
                        {a.category}
                      </span>
                      <h2 className="font-serif text-2xl md:text-[26px] text-charcoal leading-[1.15] group-hover:text-terracotta transition-colors">
                        {a.title}
                      </h2>
                      <p className="mt-4 text-charcoal/65 leading-relaxed text-[14px] flex-1">
                        {a.excerpt}
                      </p>
                      <div className="mt-6 flex items-center gap-5 text-[11px] tracking-[0.2em] uppercase text-charcoal/45">
                        <span className="inline-flex items-center gap-1.5">
                          <Calendar size={12} /> {formatDate(a.publishedAt)}
                        </span>
                        <span className="inline-flex items-center gap-1.5">
                          <Clock size={12} /> {a.readingMinutes} {t("blog.minRead")}
                        </span>
                      </div>
                      <div className="mt-6 inline-flex items-center gap-2 text-sm text-charcoal group-hover:text-terracotta transition-colors">
                        {t("blog.readArticle")}
                        <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                      </div>
                    </div>
                  </article>
                </Link>
              </Reveal>
            ))}
          </div>

          {articles.length === 1 && (
            <Reveal delay={0.2}>
              <div className="mt-16 text-center max-w-md mx-auto">
                <p className="text-charcoal/60 text-sm leading-relaxed">
                  {t("blog.morePending")}
                </p>
              </div>
            </Reveal>
          )}
        </div>
      </section>

      <section className="bg-ivory pb-24">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12 lg:px-16">
          <Reveal>
            <NewsletterSignup variant="light" source="blog" />
          </Reveal>
        </div>
      </section>

      <SectionCTA
        title={t("blog.ctaTitle")}
        body={t("blog.ctaBody")}
        primaryLabel={t("blog.ctaBtn")}
        primaryTo="/sesion-gratuita"
      />
    </div>
  );
};

export default Blog;
